import * as Dialog from '@radix-ui/react-dialog';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import type {
  AmountAdjustment,
  DepositStatus,
  DuplicateSlipClaim,
  ProposeReviewDecisionRequest,
  ReviewCaseDecision,
  TransactionTimelineEntry,
} from '@pf/contracts';
import {
  ArrowRight,
  BadgeCheck,
  Hand,
  HandCoins,
  Loader2,
  RefreshCw,
  Scale,
  ShieldAlert,
  ShieldCheck,
  UserCheck,
  XCircle,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/auth-context';
import {
  useCaseClaim,
  useConfirmDecision,
  useOpsTransaction,
  usePolicy,
  useProposeDecision,
  useRequestReverify,
  useReviewCase,
  useReviewCaseContext,
} from '@/lib/api/ops-queries';
import { formatDateTime, formatRelative } from '@/lib/datetime';
import { isPlainClick, withBasePath } from '@/lib/nav/list-state';
import { transactionReviewCasesHref } from '@/lib/nav/ops-lists';
import { REVIEWS_ORIGIN } from '@/lib/nav/return-to';
import { isProviderUnavailableReason, REVIEW_REASON_META } from '@/lib/states';
import { cn } from '@/lib/utils';
import { CheckMatrix } from '@/components/domain/check-matrix';
import { CopyButton } from '@/components/domain/copy-button';
import { SlaCountdown } from '@/components/domain/countdown-timer';
import { CustomerUploadCard } from '@/components/domain/customer-upload-card';
import { MoneyDisplay } from '@/components/domain/money-display';
import { ParticipantPair } from '@/components/domain/participant-chip';
import { PayeeDiff } from '@/components/domain/payee-diff';
import { PayerDiff } from '@/components/domain/payer-diff';
import { SlipViewer } from '@/components/domain/slip-viewer';
import { StateBadge } from '@/components/domain/state-badge';
import { StateTimeline } from '@/components/domain/state-timeline';
import { TimeAmountDiff } from '@/components/domain/time-amount-diff';
import { BackLink } from '@/components/layout/back-link';
import { Field, PageHeader, Section } from '@/components/layout/page';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Callout, ErrorState, Skeleton } from '@/components/ui/feedback';
import { Code, Label, Pill, Textarea } from '@/components/ui/primitives';

/**
 * Manual review — the case detail and the maker → checker dual-control flow.
 *
 * This is the screen where a human overrules an automated system, so it is built around one
 * question: *did this money actually reach this withdrawer?* Everything on the page serves that
 * question and nothing else — the bank-attested slip, the field-by-field payee diff, the four-way
 * check matrix, and the event trail showing how long each step took.
 *
 * Dual control is enforced in the UI the same way it is enforced on the server: the confirm
 * action is unavailable to the operator who proposed, and the reason is stated rather than the
 * button merely being greyed out. SAD §11's rule is that the checker must be a *different
 * identity*, and a console that let someone try and then failed with a 422 would be teaching the
 * wrong mental model.
 */

/**
 * One customer's username inside the case card's username field, labelled by side (ผู้ฝาก/ผู้ถอน).
 * Display handle when the website has sent one, monospace ref otherwise, "—" when neither is
 * known — an unmatched case has no withdrawer to name, and pre-0020 withdrawals have no ref.
 */
function CaseUsername({
  partyLabel,
  display,
  accountRef,
  copyLabel,
}: {
  partyLabel: string;
  display: string | null | undefined;
  accountRef: string | null | undefined;
  copyLabel: string;
}): ReactNode {
  const shown = display ?? accountRef;
  return (
    <p className="flex min-w-0 items-center gap-1">
      <span className="shrink-0 text-2xs text-muted-foreground">{partyLabel}</span>
      {shown == null ? (
        <span className="text-sm text-muted-foreground">—</span>
      ) : (
        <>
          <span
            className={
              display != null ? 'truncate text-sm font-medium' : 'truncate font-mono text-sm'
            }
          >
            {shown}
          </span>
          <CopyButton value={shown} label={copyLabel} className="size-5 shrink-0 [&_svg]:size-3" />
        </>
      )}
    </p>
  );
}

/** The case detail and the maker → checker dual-control flow — see the note above. */
export function ManualReviewPage(): ReactNode {
  const { t } = useTranslation();
  const { caseId } = useParams({ from: '/operator/ops/reviews/$caseId' });
  const reviewCase = useReviewCase(caseId);
  const context = useReviewCaseContext(caseId);
  const policy = usePolicy();
  const transaction = useOpsTransaction(reviewCase.data?.transaction_id ?? '');
  const claim = useCaseClaim(caseId);
  const { session, can } = useAuth();

  if (reviewCase.isLoading) return <ReviewSkeleton />;
  if (reviewCase.isError) {
    return (
      <>
        <BackLink fallback={REVIEWS_ORIGIN} />
        <ErrorState
          error={reviewCase.error}
          onRetry={() => {
            void reviewCase.refetch();
          }}
        />
      </>
    );
  }
  if (!reviewCase.data) return null;

  const detail = reviewCase.data;
  const reasonMeta = REVIEW_REASON_META[detail.reason];
  const dualControl = context.data?.dual_control_required ?? false;
  const isProposer = detail.proposal?.maker === session?.operator_id;
  const claimedBySomeoneElse =
    context.data?.claimed_by_id !== null &&
    context.data?.claimed_by_id !== undefined &&
    context.data.claimed_by_id !== session?.operator_id;
  const claimedBySelf =
    context.data?.claimed_by_id !== null &&
    context.data?.claimed_by_id !== undefined &&
    context.data.claimed_by_id === session?.operator_id;

  return (
    <>
      <BackLink fallback={REVIEWS_ORIGIN} />

      <PageHeader
        title={reasonMeta ? t(reasonMeta.labelKey) : detail.reason}
        description={reasonMeta ? t(reasonMeta.descriptionKey) : undefined}
        badge={<StateBadge machine="review-case" status={detail.status} />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {context.data ? <SlaCountdown dueAt={context.data.sla_due_at} /> : null}
            {context.data?.claimed_by === null ? (
              <Button
                variant="outline"
                size="sm"
                loading={claim.isPending}
                onClick={() => {
                  claim.mutate({ claim: true });
                }}
              >
                <Hand aria-hidden="true" />
                {t('manualReview.claimThisCase')}
              </Button>
            ) : (
              <>
                <Pill>
                  <UserCheck className="size-3" aria-hidden="true" />
                  {context.data?.claimed_by}
                </Pill>
                {claimedBySelf ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={claim.isPending}
                    onClick={() => {
                      claim.mutate({ claim: false });
                    }}
                  >
                    {t('manualReview.releaseClaim')}
                  </Button>
                ) : null}
              </>
            )}
          </div>
        }
      />

      {claimedBySomeoneElse ? (
        <Callout tone="warning" title={t('manualReview.claimedBySomeoneElse.title')}>
          <Trans
            i18nKey="manualReview.claimedBySomeoneElse.body"
            values={{ operator: context.data?.claimed_by }}
          />
        </Callout>
      ) : null}

      {detail.duplicate_of ? <DuplicateSlipCallout claim={detail.duplicate_of} /> : null}

      {claim.isError ? <ErrorState error={claim.error} /> : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
        <div className="space-y-6">
          <Section
            title={t('manualReview.facts.title')}
            description={t('manualReview.facts.description')}
          >
            <Card>
              <CardContent className="space-y-4 pt-5">
                {/*
                 * The transaction id gets its own full-width band rather than a quarter of the
                 * stats grid. It is the one value on this screen an operator carries elsewhere —
                 * into a bank portal, a ticket, a chat with the participating website — and at
                 * `grid-cols-4` a 30-character ULID broke across three lines of `text-xs` muted
                 * type, which reads as a footnote rather than as the case's identity.
                 */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-border bg-muted/30 px-3 py-2">
                  <span className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('manualReview.fields.transaction')}
                  </span>
                  <Code className="min-w-0 break-all bg-transparent px-0 text-sm font-medium text-foreground">
                    {detail.transaction_id}
                  </Code>
                  <CopyButton
                    value={detail.transaction_id}
                    label={t('manualReview.fields.transaction')}
                    className="size-6 shrink-0 [&_svg]:size-3.5"
                  />
                </div>

                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <Field label={t('manualReview.fields.amount')}>
                    {context.data ? (
                      <MoneyDisplay amount={context.data.amount} size="lg" />
                    ) : (
                      <Skeleton className="h-6 w-20" />
                    )}
                  </Field>
                  <Field
                    label={t('manualReview.fields.slipAttempts')}
                    hint={t('manualReview.fields.slipAttemptsHint')}
                  >
                    <span className="tnum text-sm">
                      {t('manualReview.fields.slipAttemptsValue', {
                        attempts: context.data?.evidence_attempts ?? 0,
                        max: policy.data?.max_evidence_resubmit_attempts ?? 3,
                      })}
                    </span>
                  </Field>
                  <Field label={t('manualReview.fields.opened')}>
                    <span className="text-sm" title={formatDateTime(detail.opened_at)}>
                      {formatRelative(detail.opened_at)}
                    </span>
                  </Field>
                  <Field
                    label={t('manualReview.fields.username')}
                    hint={t('manualReview.fields.usernameHint')}
                  >
                    {context.data === undefined ? (
                      <Skeleton className="h-5 w-24" />
                    ) : (
                      // Both customers, stacked like History's username column: the case decides
                      // a payout *to* the withdrawer, so naming only the depositor was half the
                      // card — and on a cross-website match the two usernames come from
                      // different sites' directories entirely.
                      <div className="min-w-0 space-y-0.5">
                        <CaseUsername
                          partyLabel={t('manualReview.fields.usernameDepositor')}
                          display={context.data.account_display}
                          accountRef={context.data.account_ref}
                          copyLabel={t('manualReview.fields.username')}
                        />
                        <CaseUsername
                          partyLabel={t('manualReview.fields.usernameWithdrawer')}
                          display={context.data.withdrawer_account_display}
                          accountRef={context.data.withdrawer_account_ref}
                          copyLabel={t('manualReview.fields.username')}
                        />
                      </div>
                    )}
                  </Field>
                  {/* Since cross-website matching, the depositor's own website is only half the
                      story — the withdrawal this case decides a payout on can belong to another
                      site entirely, and nothing else on this card says so. */}
                  <Field label={t('manualReview.fields.website')} hint={t('manualReview.fields.websiteHint')}>
                    {context.data === undefined ? (
                      <Skeleton className="h-5 w-24" />
                    ) : (
                      <ParticipantPair
                        depositor={context.data.participant}
                        withdrawer={context.data.withdrawal_participant}
                      />
                    )}
                  </Field>
                </dl>
              </CardContent>
            </Card>
          </Section>

          {/*
           * ต้นทาง first, ปลายทาง second — the order the operator reads the slip in, and the
           * order the question is asked in: *who sent this*, then *did it land where we said*.
           * The sender is also the cheaper disqualifier: a slip from an unregistered account
           * ends the review regardless of how perfect the receiver looks.
           */}
          <Section
            title={t('manualReview.payerDiff.title')}
            description={t('manualReview.payerDiff.description')}
          >
            <PayerDiff
              expected={detail.expected_payer ?? null}
              actual={detail.slip}
              nameScore={detail.payer_name_score}
            />
          </Section>

          <Section
            title={t('manualReview.payeeDiff.title')}
            description={t('manualReview.payeeDiff.description')}
          >
            <PayeeDiff
              expected={detail.expected_payee}
              actual={detail.slip}
              nameScore={context.data?.name_score}
            />
          </Section>

          <Section
            title={t('manualReview.timeAmountDiff.title')}
            description={t('manualReview.timeAmountDiff.description')}
          >
            <TimeAmountDiff
              reservedAt={detail.reserved_at}
              expectedAmount={context.data?.amount ?? { value: '0.00', currency: 'THB' }}
              slip={detail.slip}
            />
          </Section>

          <Section
            title={t('manualReview.checks.title')}
            description={t('manualReview.checks.description')}
          >
            {/*
             * `detail.checks` — the check matrix riding alongside the review case's own slip —
             * is preferred over `transaction.data.verification`: that endpoint's own projection
             * shares the bug this screen exists to fix (it also requires a claimed `trans_ref`),
             * so a fraud-class shadow/duplicate case would still show "no provider result" there
             * even once `GetReviewCaseDetail` is corrected. Falling back to it only for an older
             * server that has not yet added `checks` to the review-case detail response.
             */}
            {detail.checks ? (
              <CheckMatrix
                checks={detail.checks}
                nameScore={context.data?.name_score}
                nameThreshold={policy.data?.name_match_auto_approve_threshold ?? 0.9}
              />
            ) : transaction.isLoading ? (
              <Skeleton className="h-40" />
            ) : transaction.data?.verification ? (
              <CheckMatrix
                checks={transaction.data.verification.checks}
                nameScore={context.data?.name_score}
                nameThreshold={policy.data?.name_match_auto_approve_threshold ?? 0.9}
              />
            ) : (
              <Callout tone="warning" title={t('manualReview.checks.noResultTitle')}>
                {t('manualReview.checks.noResultBody')}
              </Callout>
            )}
          </Section>

          <Section
            title={t('manualReview.decisionSection.title')}
            description={t('manualReview.decisionSection.description')}
          >
            {/*
             * "ตรวจสอบใหม่" sits above the verdicts, not among them, because it is not a verdict:
             * a `PROVIDER_DOWN` case is open because nobody could *ask*, and the honest first move
             * is to ask again. Offered only for that reason — every other case is open because
             * somebody doubted the answer, and re-asking would not address the doubt.
             */}
            {isProviderUnavailableReason(detail.reason) ||
            detail.reverifiable === true ||
            (detail.status !== 'OPEN' &&
              transaction.data?.events.some((e) => e.type === 'review.reverify_requested') ===
                true) ? (
              <ReverifyPanel
                caseId={caseId}
                transactionId={detail.transaction_id}
                reason={detail.reason}
                status={detail.status}
                /*
                 * A close with no proposal on a provider-outage case is a "ตรวจสอบใหม่" close —
                 * the server records the `REVERIFY` outcome with `final_decision = NULL`, so the
                 * absent proposal *is* the signal. Anything the panel says after this point is
                 * read off the transaction, which is the half still moving.
                 */
                closedByReverify={
                  detail.proposal === null &&
                  (detail.status === 'CLOSED' || detail.status === 'CONFIRMED')
                }
                transactionStatus={transaction.data?.status ?? null}
                transactionEvents={transaction.data?.events ?? []}
                canReverify={can('MAKER')}
                claimedByOther={claimedBySomeoneElse ? (context.data?.claimed_by ?? null) : null}
              />
            ) : null}

            <DecisionPanel
              caseId={caseId}
              reason={detail.reason}
              status={detail.status}
              proposal={detail.proposal}
              confirmation={detail.confirmation}
              dualControl={dualControl}
              isProposer={isProposer}
              canPropose={can('MAKER')}
              canConfirm={can('CHECKER')}
              threshold={policy.data?.dual_control_threshold ?? '2000.00'}
              claimedByOther={claimedBySomeoneElse ? (context.data?.claimed_by ?? null) : null}
              adjustmentPreview={detail.adjustment_preview ?? null}
            />
          </Section>
        </div>

        <div className="space-y-6">
          <CustomerUploadCard transactionId={detail.transaction_id} />

          <Section
            title={t('manualReview.slip.title')}
            description={t('manualReview.slip.description')}
          >
            <SlipViewer slip={detail.slip} />
          </Section>

          <Card>
            <CardHeader>
              <CardTitle>{t('manualReview.eventTimeline')}</CardTitle>
            </CardHeader>
            <CardContent>
              {transaction.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }, (_, i) => (
                    <Skeleton key={i} className="h-8" />
                  ))}
                </div>
              ) : (
                <StateTimeline events={transaction.data?.events ?? []} dense />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

/**
 * "สลิปนี้ถูกใช้ไปแล้ว" — the backlink to the order that holds this slip's bank reference.
 *
 * A `trans_ref` is claimed once system-wide, so a case reached over a contested reference has a
 * *counterpart*, and every question the operator is about to ask is really about that other order:
 * was it credited, is it the same depositor, is it even the same payout. Until this panel existed
 * the answer was a hand-written query against the evidence table — which is how both live cases
 * that motivated it were resolved, hours apart from the decision they were meant to inform.
 *
 * Two tones, because the two shapes are not the same accusation:
 *
 *  - **Same withdrawal** — the depositor's own earlier order died and they re-sent the payment
 *    they really made. A bookkeeping question: has this transfer already been credited once?
 *  - **Different withdrawal, or unknown** — a slip is being replayed against a payout it never
 *    paid. `same_withdrawal: false` also carries "could not be established", and both deserve the
 *    louder tone: the reassuring reading is the one that needs proof.
 *
 * The link is rendered iff a *deposit* holds the claim. A settlement's slip (the clearing side
 * shares the index) is named rather than linked, because no screen takes a settlement id.
 */
function DuplicateSlipCallout({ claim }: { claim: DuplicateSlipClaim }): ReactNode {
  const { t } = useTranslation();
  const key = claim.same_withdrawal ? 'sameWithdrawal' : 'foreign';

  return (
    <Callout
      tone={claim.same_withdrawal ? 'warning' : 'danger'}
      title={t(`manualReview.duplicateSlip.${key}.title`)}
    >
      <p>{t(`manualReview.duplicateSlip.${key}.body`)}</p>

      <dl className="mt-2 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('manualReview.duplicateSlip.claimedBy')}
          </dt>
          <dd className="flex min-w-0 flex-wrap items-center gap-1.5">
            {claim.transaction_id === null ? (
              <span className="text-sm">
                {t('manualReview.duplicateSlip.settlementOwner', {
                  id: claim.settlement_id ?? '—',
                })}
              </span>
            ) : (
              <>
                <Link
                  to="/ops/history/$transactionId"
                  params={{ transactionId: claim.transaction_id }}
                  className="min-w-0 break-all font-mono text-sm font-medium underline underline-offset-2"
                >
                  {claim.transaction_id}
                </Link>
                <CopyButton
                  value={claim.transaction_id}
                  label={t('manualReview.duplicateSlip.claimedBy')}
                  className="size-5 shrink-0 [&_svg]:size-3"
                />
                {claim.status === null ? null : (
                  <StateBadge machine="deposit" status={claim.status} size="sm" />
                )}
              </>
            )}
          </dd>
        </div>

        <div className="min-w-0">
          <dt className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('manualReview.duplicateSlip.transRef')}
          </dt>
          <dd className="flex min-w-0 items-center gap-1.5">
            <Code className="min-w-0 break-all bg-transparent px-0 text-sm">{claim.trans_ref}</Code>
            <CopyButton
              value={claim.trans_ref}
              label={t('manualReview.duplicateSlip.transRef')}
              className="size-5 shrink-0 [&_svg]:size-3"
            />
          </dd>
        </div>

        {claim.amount === null ? null : (
          <div>
            <dt className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('manualReview.duplicateSlip.otherAmount')}
            </dt>
            <dd>
              <MoneyDisplay amount={claim.amount} size="sm" />
            </dd>
          </div>
        )}

        {claim.claimed_at === null ? null : (
          <div>
            <dt className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('manualReview.duplicateSlip.claimedAt')}
            </dt>
            <dd className="text-sm" title={formatDateTime(claim.claimed_at)}>
              {formatRelative(claim.claimed_at)}
            </dd>
          </div>
        )}

        {claim.depositor_account_ref === null ? null : (
          <div className="min-w-0">
            <dt className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('manualReview.duplicateSlip.otherDepositor')}
            </dt>
            <dd className="truncate font-mono text-sm">{claim.depositor_account_ref}</dd>
          </div>
        )}

        {claim.withdrawal_id === null ? null : (
          <div className="min-w-0">
            <dt className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('manualReview.duplicateSlip.otherWithdrawal')}
            </dt>
            <dd className="truncate font-mono text-sm">{claim.withdrawal_id}</dd>
          </div>
        )}
      </dl>
    </Callout>
  );
}

/**
 * "ตรวจสอบใหม่" — send a provider-outage case back through verification.
 *
 * The one action on this screen that is not a verdict. A `PROVIDER_DOWN` case is open because the
 * circuit breaker or an unreachable provider meant verification never got an answer — the slip was
 * never doubted, it was never *asked about*. So the panel is deliberately built to look unlike the
 * decision card below it: no outcome radios, no reason box, no maker/checker ceremony (the server
 * keeps this outside dual control for the same reason — nobody is asserting anything about the
 * money), and copy that says in both languages that it decides nothing.
 *
 * Only offered while the case is still `OPEN`. Once a maker has proposed, the question in front of
 * the operator is that proposal, and the server refuses a re-verify on a case that is not open.
 */
function ReverifyPanel({
  caseId,
  transactionId,
  reason,
  status,
  closedByReverify,
  transactionStatus,
  transactionEvents,
  canReverify,
  claimedByOther,
}: {
  caseId: string;
  /** The transaction this case is about — the queue link's filter, and what the outcome reads. */
  transactionId: string;
  reason: string;
  status: string;
  /** True once this case has closed with the `REVERIFY` outcome — see the call site. */
  closedByReverify: boolean;
  /** Live transaction status, or `null` until the transaction query's first response lands. */
  transactionStatus: DepositStatus | null;
  /** The transaction's event timeline, used only to tell a *new* review episode from this one. */
  transactionEvents: readonly TransactionTimelineEntry[];
  /** `review:reverify` — Maker, Checker or Super, mirrored here through `can('MAKER')`. */
  canReverify: boolean;
  /** A different operator's display name while they hold the claim, else `null`. */
  claimedByOther: string | null;
}): ReactNode {
  const { t } = useTranslation();
  const reverify = useRequestReverify(caseId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // The two halves of one story, and only ever one of them at a time: before the button is
  // pressed the case is OPEN and the panel offers the action; after it, the case is closed and
  // the panel follows the transaction until it settles.
  if (status !== 'OPEN') {
    return closedByReverify ? (
      <ReverifyOutcome
        transactionId={transactionId}
        transactionStatus={transactionStatus}
        transactionEvents={transactionEvents}
      />
    ) : null;
  }

  const blocked = !canReverify || claimedByOther !== null;

  return (
    <Card className="border-state-flight/30 bg-state-flight-bg/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="size-4 text-state-flight" aria-hidden="true" />
          {t('manualReview.reverify.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm leading-relaxed text-foreground/80">
          {isProviderUnavailableReason(reason)
            ? t('manualReview.reverify.body')
            : t('manualReview.reverify.bodyUnreadable')}
        </p>

        {claimedByOther !== null ? (
          <Callout tone="warning" title={t('manualReview.decisionLocked.title')}>
            {t('manualReview.decisionLocked.body', { operator: claimedByOther })}
          </Callout>
        ) : !canReverify ? (
          <Callout tone="info" title={t('manualReview.reverify.needsRoleTitle')}>
            {t('manualReview.reverify.needsRoleBody')}
          </Callout>
        ) : null}

        {reverify.isError ? <ErrorState error={reverify.error} /> : null}

        <Button
          variant="outline"
          disabled={blocked}
          loading={reverify.isPending}
          onClick={() => {
            setConfirmOpen(true);
          }}
        >
          <RefreshCw aria-hidden="true" />
          {t('manualReview.reverify.action')}
        </Button>

        <p className="text-2xs leading-relaxed text-muted-foreground">
          {t('manualReview.reverify.note')}
        </p>

        <Dialog.Root
          open={confirmOpen}
          onOpenChange={(open) => {
            if (!open) setConfirmOpen(false);
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-black/80" />
            <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-5 shadow-lg focus:outline-none">
              <Dialog.Title className="text-sm font-semibold">
                {t('manualReview.reverify.dialogTitle')}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {t('manualReview.reverify.dialogBody')}
              </Dialog.Description>

              {reverify.isError ? (
                <div className="mt-3">
                  <ErrorState error={reverify.error} />
                </div>
              ) : null}

              <div className="mt-4 flex justify-end gap-2">
                <Dialog.Close asChild>
                  <Button variant="ghost" size="sm">
                    {t('manualReview.reverify.dialogDismiss')}
                  </Button>
                </Dialog.Close>
                <Button
                  size="sm"
                  loading={reverify.isPending}
                  onClick={() => {
                    reverify.mutate(undefined, {
                      onSuccess: () => {
                        setConfirmOpen(false);
                      },
                    });
                  }}
                >
                  <RefreshCw aria-hidden="true" />
                  {t('manualReview.reverify.dialogConfirm')}
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </CardContent>
    </Card>
  );
}

/**
 * What a re-verification came back with, as one of six stories the operator can act on.
 *
 * Derived from the transaction's *status* rather than from a flag anyone sets: the status is the
 * projection of the event log, and the log is the record of truth (SAD §13). The `switch` is total
 * over `DepositStatus`, so a tenth deposit state is a compile error here rather than an outcome
 * card that quietly says the wrong thing — the same reason `appliedLabelKey` below is a switch.
 */
type ReverifyOutcomeKind =
  /** Still with the provider, or not yet read back. */
  | 'pending'
  /** Attested — the transaction passed and is crediting on its own. */
  | 'approved'
  /** Mismatch: the depositor may send another slip. */
  | 'resubmit'
  /** Inconclusive: back under manual review, under a *different* case than this page. */
  | 'reopened'
  /** Under review, but nothing says a new episode started — see {@link reopenedAfterReverify}. */
  | 'stillUnderReview'
  /** Fraud-class: rejected outright. */
  | 'rejected'
  /** Somewhere the re-verification does not explain. The state badge is then the whole story. */
  | 'moved';

function reverifyOutcomeKind(status: DepositStatus | null, reopened: boolean): ReverifyOutcomeKind {
  if (status === null) return 'pending';
  switch (status) {
    case 'VERIFYING':
      return 'pending';
    case 'CONFIRMED':
    case 'CREDIT_PENDING':
    case 'COMPLETED':
      return 'approved';
    case 'AWAITING_EVIDENCE':
      return 'resubmit';
    case 'UNDER_REVIEW':
      return reopened ? 'reopened' : 'stillUnderReview';
    case 'REJECTED':
      return 'rejected';
    case 'RESERVED':
    case 'EXPIRED':
      return 'moved';
  }
}

/**
 * Whether the transaction entered review *again* after the re-verify was requested.
 *
 * The one thing the operator cannot see from a status alone: `UNDER_REVIEW` reads the same whether
 * it is the episode this closed case belongs to or a brand-new one the provider's inconclusive
 * answer opened. Sequence numbers settle it — an `transaction.under_review` after the last
 * `review.reverify_requested` is a new episode, and therefore a new case id this page cannot show.
 *
 * Conservative by construction: with no re-verify event on the timeline (an older transaction, or
 * a feed that has not caught up) it answers `false`, and the panel says only what it can prove.
 */
function reopenedAfterReverify(events: readonly TransactionTimelineEntry[]): boolean {
  let requestedAt = -1;
  for (const event of events) {
    if (event.type === 'review.reverify_requested' && event.sequence > requestedAt) {
      requestedAt = event.sequence;
    }
  }
  if (requestedAt < 0) return false;
  return events.some(
    (event) => event.type === 'transaction.under_review' && event.sequence > requestedAt,
  );
}

/** The i18n key carrying the body copy for each outcome. */
function reverifyOutcomeKey(kind: ReverifyOutcomeKind): string {
  return `manualReview.reverify.outcome.${kind}`;
}

/**
 * The live result of a "ตรวจสอบใหม่", on the page the operator is already standing on.
 *
 * Before this existed the screen stopped at "sent" and stayed there: the action closes the case,
 * so the case poll stopped at the exact moment the interesting part began, and the operator had to
 * reload to learn anything. The transaction is what keeps moving, so it is what this reads —
 * `useOpsTransaction` polls it while it is in flight and stops when it settles.
 *
 * The third outcome is the one this card exists for. When the provider is still unreachable or its
 * answer is ambiguous the transaction goes back under review *under a new case id*, which means
 * reloading this page does not reach it — the operator would be re-reading a closed case forever.
 * `GET /ops/v1/transactions/{id}` carries no review-case id (`TransactionDetailSchema` has no such
 * field), so rather than invent one the card links to the review queue filtered by transaction id,
 * where the new case is the open row.
 */
function ReverifyOutcome({
  transactionId,
  transactionStatus,
  transactionEvents,
}: {
  transactionId: string;
  transactionStatus: DepositStatus | null;
  transactionEvents: readonly TransactionTimelineEntry[];
}): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const kind = reverifyOutcomeKind(transactionStatus, reopenedAfterReverify(transactionEvents));
  const href = transactionReviewCasesHref(transactionId);
  const tone =
    kind === 'approved'
      ? 'border-state-good/30 bg-state-good-bg/30'
      : kind === 'rejected'
        ? 'border-state-bad/30 bg-state-bad-bg/30'
        : kind === 'reopened' || kind === 'resubmit' || kind === 'stillUnderReview'
          ? 'border-state-attention/30 bg-state-attention-bg/30'
          : 'border-state-flight/30 bg-state-flight-bg/30';

  return (
    <Card className={tone}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {kind === 'pending' ? (
            <Loader2 className="size-4 animate-spin text-state-flight" aria-hidden="true" />
          ) : (
            <RefreshCw className="size-4 text-muted-foreground" aria-hidden="true" />
          )}
          {t('manualReview.reverify.outcomeTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm leading-relaxed text-foreground/80">{t(reverifyOutcomeKey(kind))}</p>

        {transactionStatus === null ? null : (
          <Field label={t('manualReview.reverify.currentStatus')}>
            <StateBadge machine="deposit" status={transactionStatus} size="sm" />
          </Field>
        )}

        {/*
         * Offered on every review outcome, not only the new-case one: when the transaction is
         * under review the operator's next move is the *live* case, and this page is never it.
         */}
        {kind === 'reopened' || kind === 'stillUnderReview' ? (
          <div className="space-y-2">
            <Button asChild variant="outline" size="sm">
              <a
                href={withBasePath(href)}
                onClick={(event) => {
                  if (!isPlainClick(event)) return;
                  event.preventDefault();
                  void navigate({ href });
                }}
              >
                <ArrowRight aria-hidden="true" />
                {t('manualReview.reverify.openQueueLink')}
              </a>
            </Button>
            <p className="text-2xs leading-relaxed text-muted-foreground">
              {t('manualReview.reverify.openQueueNote')}
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * The verdicts a maker can propose — derived from the request contract rather than restated, so
 * the radio group and the server's enum cannot drift. `REVERIFY` is excluded there on purpose: it
 * is a *close outcome* reached through `POST /review-cases/{id}/reverify`, not a proposal.
 */
type Decision = ProposeReviewDecisionRequest['decision'];

/**
 * The i18n key for a decision that has already been applied.
 *
 * An exhaustive `switch` rather than the chain of ternaries this replaces: the chain's `else` arm
 * was "rejected", so every outcome the contract gained after it was written — `EXTERNAL`,
 * `APPROVE_ADJUSTED`, now `REVERIFY` — would have quietly told the operator the deposit had been
 * rejected. Here a new `ReviewCaseDecision` is a compile error instead.
 */
function appliedLabelKey(decision: ReviewCaseDecision): string {
  switch (decision) {
    case 'APPROVE':
      return 'manualReview.decision.approvedApplied';
    case 'REJECT':
      return 'manualReview.decision.rejectedApplied';
    case 'EXTERNAL':
      return 'manualReview.decision.externalApplied';
    case 'APPROVE_ADJUSTED':
      return 'manualReview.decision.adjustedApplied';
    case 'REVERIFY':
      return 'manualReview.reverify.requested';
  }
}

/** The i18n key naming a decision a maker has proposed and a checker is being asked to apply. */
function proposedLabelKey(decision: ReviewCaseDecision): string {
  switch (decision) {
    case 'APPROVE':
      return 'manualReview.awaitingChecker.approve';
    case 'REJECT':
      return 'manualReview.awaitingChecker.reject';
    case 'EXTERNAL':
      return 'manualReview.awaitingChecker.external';
    case 'APPROVE_ADJUSTED':
      return 'manualReview.awaitingChecker.adjusted';
    // Unreachable through the maker path — the request schema excludes it — but the switch has to
    // be total, and naming it honestly beats defaulting it into somebody else's verdict.
    case 'REVERIFY':
      return 'manualReview.reverify.requested';
  }
}

/** The i18n key for the checker's confirm button, which restates what it is about to apply. */
function confirmLabelKey(decision: ReviewCaseDecision): string {
  switch (decision) {
    case 'APPROVE':
      return 'manualReview.awaitingChecker.confirmApproval';
    case 'REJECT':
      return 'manualReview.awaitingChecker.confirmRejection';
    case 'EXTERNAL':
      return 'manualReview.awaitingChecker.confirmExternal';
    case 'APPROVE_ADJUSTED':
      return 'manualReview.awaitingChecker.confirmAdjusted';
    case 'REVERIFY':
      return 'manualReview.reverify.dialogConfirm';
  }
}

function DecisionPanel({
  caseId,
  reason,
  status,
  proposal,
  confirmation,
  dualControl,
  isProposer,
  canPropose,
  canConfirm,
  threshold,
  claimedByOther,
  adjustmentPreview,
}: {
  caseId: string;
  /** The case's review reason, so a case closed without a proposal can say *why* it closed. */
  reason: string;
  status: string;
  proposal: {
    maker: string;
    /** The *applied* outcome vocabulary, which is wider than {@link Decision} — see `REVERIFY`. */
    decision: ReviewCaseDecision;
    note: string;
    proposed_at: string;
  } | null;
  confirmation: { checker: string; confirmed_at: string } | null;
  dualControl: boolean;
  isProposer: boolean;
  canPropose: boolean;
  canConfirm: boolean;
  threshold: string;
  /**
   * The display name of a *different* operator holding the claim, or `null`. The server refuses
   * the *maker* step on a case claimed by somebody else (409 `CASE_CLAIMED`); the panel disables
   * the propose controls and says who holds it. The confirm step is deliberately NOT locked —
   * the checker must be a different operator, so the maker's own claim can never block them.
   */
  claimedByOther: string | null;
  /**
   * The server-computed "เกลี่ยยอด" settle an APPROVE_ADJUSTED decision would apply, or `null`
   * when none is possible — which is exactly when the option must not be offered. The console
   * never computes money: it renders what the server said it would do.
   */
  adjustmentPreview: AmountAdjustment | null;
}): ReactNode {
  const { t } = useTranslation();
  const propose = useProposeDecision(caseId);
  const confirm = useConfirmDecision(caseId);
  const [decision, setDecision] = useState<Decision>('APPROVE');
  const [note, setNote] = useState('');
  // The EXTERNAL confirmation dialog — the one decision that must restate its consequences
  // ("the system will NOT credit the depositor") before it is proposed.
  const [externalOpen, setExternalOpen] = useState(false);

  if (status === 'CONFIRMED' || status === 'CLOSED') {
    return (
      <Card className="border-state-good/30 bg-state-good-bg/30">
        <CardContent className="space-y-4 pt-5">
          <p className="flex items-center gap-2 text-sm font-semibold text-state-good">
            <BadgeCheck className="size-4" aria-hidden="true" />
            {/*
             * A case can now close with no proposal on it at all: "ตรวจสอบใหม่" closes it without
             * anyone deciding the money. Falling through to `rejectedApplied` would tell the
             * operator their re-verify had rejected the deposit, which is the opposite of true.
             *
             * This card is the *record* — what was done to the case, and it is finished. What the
             * provider then answered is live, changes after this card is drawn, and belongs to
             * `ReverifyOutcome` above; saying "awaiting the provider" here would keep claiming a
             * wait that has already ended.
             */}
            {proposal === null
              ? isProviderUnavailableReason(reason) || reason === 'INCOMPLETE_DATA'
                ? t('manualReview.reverify.closedApplied')
                : t('manualReview.decision.closedApplied')
              : t(appliedLabelKey(proposal.decision))}
          </p>
          {proposal ? (
            <blockquote className="border-l-2 border-border pl-3 text-sm leading-relaxed text-foreground/80">
              {proposal.note}
            </blockquote>
          ) : null}
          <dl className="grid gap-4 sm:grid-cols-2">
            {proposal ? (
              <Field
                label={t('manualReview.decision.proposedBy')}
                hint={formatDateTime(proposal.proposed_at)}
              >
                <Code>{proposal.maker}</Code>
              </Field>
            ) : null}
            {confirmation ? (
              <Field
                label={t('manualReview.decision.confirmedBy')}
                hint={formatDateTime(confirmation.confirmed_at)}
              >
                <Code>{confirmation.checker}</Code>
              </Field>
            ) : null}
          </dl>
          <p className="text-2xs leading-relaxed text-muted-foreground">
            {t('manualReview.decision.auditNote')}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (status === 'PROPOSED' && proposal) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="size-4 text-state-attention" aria-hidden="true" />
            {t('manualReview.awaitingChecker.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 rounded-lg border border-border p-4">
            <p className="text-xs font-medium text-muted-foreground">
              {t('manualReview.awaitingChecker.proposedDecisionLabel')}{' '}
              <span
                className={cn(
                  'font-semibold',
                  proposal.decision === 'APPROVE'
                    ? 'text-state-good'
                    : proposal.decision === 'EXTERNAL' || proposal.decision === 'APPROVE_ADJUSTED'
                      ? 'text-state-attention'
                      : 'text-state-bad',
                )}
              >
                {t(proposedLabelKey(proposal.decision))}
              </span>
            </p>
            <blockquote className="border-l-2 border-border pl-3 text-sm leading-relaxed">
              {proposal.note}
            </blockquote>
            <p className="text-2xs text-muted-foreground">
              <Trans
                i18nKey="manualReview.awaitingChecker.proposedBy"
                values={{ maker: proposal.maker, when: formatRelative(proposal.proposed_at) }}
                components={{ code: <Code /> }}
              />
            </p>
          </div>

          {isProposer ? (
            <Callout tone="warning" title={t('manualReview.awaitingChecker.isProposerTitle')}>
              {t('manualReview.awaitingChecker.isProposerBody')}
            </Callout>
          ) : !canConfirm ? (
            <Callout tone="info" title={t('manualReview.awaitingChecker.needsCheckerTitle')}>
              {t('manualReview.awaitingChecker.needsCheckerBody')}
            </Callout>
          ) : null}

          {confirm.isError ? <ErrorState error={confirm.error} /> : null}

          <Button
            disabled={isProposer || !canConfirm}
            loading={confirm.isPending}
            onClick={() => {
              confirm.mutate();
            }}
          >
            <ShieldCheck aria-hidden="true" />
            {t(confirmLabelKey(proposal.decision))}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('manualReview.propose.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {dualControl ? (
          <Callout tone="warning" title={t('manualReview.propose.dualControlTitle')}>
            {t('manualReview.propose.dualControlBody', { threshold })}
          </Callout>
        ) : null}

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">{t('manualReview.propose.outcomeLegend')}</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              {
                value: 'APPROVE' as const,
                label: t('manualReview.propose.approveLabel'),
                body: t('manualReview.propose.approveBody'),
                icon: BadgeCheck,
                tone: 'good' as const,
                wide: false,
              },
              {
                value: 'REJECT' as const,
                label: t('manualReview.propose.rejectLabel'),
                body: t('manualReview.propose.rejectBody'),
                icon: XCircle,
                tone: 'bad' as const,
                wide: false,
              },
              // "เติมมือแล้ว" — the admin already credited the depositor by hand outside the
              // rails. Full-width, because it is the odd one out and its body carries the warning.
              {
                value: 'EXTERNAL' as const,
                label: t('manualReview.propose.externalLabel'),
                body: t('manualReview.propose.externalBody'),
                icon: HandCoins,
                tone: 'attention' as const,
                wide: true,
              },
              // "เกลี่ยยอด" — settle at what the slip proves. Offered only when the server
              // computed a preview; the amounts shown are exactly what the decision will apply.
              ...(adjustmentPreview === null
                ? []
                : [
                    {
                      value: 'APPROVE_ADJUSTED' as const,
                      label: t('manualReview.propose.adjustedLabel'),
                      body:
                        adjustmentPreview.direction === 'UNDER'
                          ? t('manualReview.propose.adjustedBodyUnder')
                          : t('manualReview.propose.adjustedBodyOver'),
                      icon: Scale,
                      tone: 'attention' as const,
                      wide: true,
                      extra: (
                        <span className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
                          <span className="inline-flex items-baseline gap-1.5 text-2xs text-muted-foreground">
                            {t('manualReview.propose.adjustedPaid')}
                            <MoneyDisplay amount={adjustmentPreview.paid_amount} size="sm" />
                          </span>
                          <span className="inline-flex items-baseline gap-1.5 text-2xs text-muted-foreground">
                            {t('manualReview.propose.adjustedReserved')}
                            <MoneyDisplay amount={adjustmentPreview.reserved_amount} size="sm" />
                          </span>
                          <span className="inline-flex items-baseline gap-1.5 text-2xs text-muted-foreground">
                            {t('manualReview.propose.adjustedDelta')}
                            <MoneyDisplay amount={adjustmentPreview.delta} size="sm" />
                          </span>
                        </span>
                      ),
                    },
                  ]),
            ].map(({ value, label, body, icon: Icon, tone, wide, ...option }) => (
              <label
                key={value}
                className={cn(
                  'flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors',
                  'focus-within:ring-2 focus-within:ring-ring',
                  wide && 'sm:col-span-2',
                  decision === value
                    ? tone === 'good'
                      ? 'border-state-good/50 bg-state-good-bg'
                      : tone === 'attention'
                        ? 'border-state-attention/50 bg-state-attention-bg'
                        : 'border-state-bad/50 bg-state-bad-bg'
                    : 'border-border hover:bg-muted/50',
                )}
              >
                <input
                  type="radio"
                  name="decision"
                  value={value}
                  checked={decision === value}
                  onChange={() => {
                    setDecision(value);
                  }}
                  className="sr-only"
                />
                <Icon
                  className={cn(
                    'mt-0.5 size-4 shrink-0',
                    tone === 'good'
                      ? 'text-state-good'
                      : tone === 'attention'
                        ? 'text-state-attention'
                        : 'text-state-bad',
                  )}
                  aria-hidden="true"
                />
                <span className="min-w-0 space-y-0.5">
                  <span className="block text-sm font-medium">{label}</span>
                  <span className="block text-2xs leading-relaxed text-muted-foreground">
                    {body}
                  </span>
                  {'extra' in option ? option.extra : null}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="space-y-1.5">
          <Label htmlFor="decision-note">{t('manualReview.propose.reasonLabel')}</Label>
          <Textarea
            id="decision-note"
            required
            value={note}
            placeholder={t('manualReview.propose.reasonPlaceholder')}
            aria-describedby="decision-note-hint"
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
          <p id="decision-note-hint" className="text-2xs text-muted-foreground">
            {t('manualReview.propose.reasonHint')}
          </p>
        </div>

        {claimedByOther !== null ? (
          <Callout tone="warning" title={t('manualReview.decisionLocked.title')}>
            {t('manualReview.decisionLocked.body', { operator: claimedByOther })}
          </Callout>
        ) : !canPropose ? (
          <Callout tone="info" title={t('manualReview.propose.needsRoleTitle')}>
            {t('manualReview.propose.needsRoleBody')}
          </Callout>
        ) : null}

        {propose.isError ? <ErrorState error={propose.error} /> : null}

        <Button
          disabled={!canPropose || claimedByOther !== null || note.trim().length < 8}
          loading={propose.isPending}
          onClick={() => {
            // EXTERNAL never fires from the bare button: it must restate its consequences first.
            if (decision === 'EXTERNAL') {
              setExternalOpen(true);
              return;
            }
            propose.mutate({ decision, note: note.trim() });
          }}
        >
          {decision === 'EXTERNAL' ? (
            <HandCoins aria-hidden="true" />
          ) : decision === 'APPROVE_ADJUSTED' ? (
            <Scale aria-hidden="true" />
          ) : (
            <ShieldCheck aria-hidden="true" />
          )}
          {decision === 'APPROVE'
            ? t('manualReview.propose.proposeApproval')
            : decision === 'EXTERNAL'
              ? t('manualReview.propose.proposeExternal')
              : decision === 'APPROVE_ADJUSTED'
                ? t('manualReview.propose.proposeAdjusted')
                : t('manualReview.propose.proposeRejection')}
        </Button>

        <Dialog.Root
          open={externalOpen}
          onOpenChange={(open) => {
            if (!open) setExternalOpen(false);
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-black/80" />
            <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-5 shadow-lg focus:outline-none">
              <Dialog.Title className="text-sm font-semibold">
                {t('manualReview.externalDialog.title')}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {t('manualReview.externalDialog.body')}
              </Dialog.Description>

              {/* The reason travels with the proposal exactly as typed — show it one last time. */}
              <blockquote className="mt-3 border-l-2 border-border pl-3 text-sm leading-relaxed">
                {note.trim()}
              </blockquote>

              {propose.isError ? (
                <div className="mt-3">
                  <ErrorState error={propose.error} />
                </div>
              ) : null}

              <div className="mt-4 flex justify-end gap-2">
                <Dialog.Close asChild>
                  <Button variant="ghost" size="sm">
                    {t('manualReview.externalDialog.dismiss')}
                  </Button>
                </Dialog.Close>
                <Button
                  size="sm"
                  loading={propose.isPending}
                  onClick={() => {
                    propose.mutate(
                      { decision: 'EXTERNAL', note: note.trim() },
                      {
                        onSuccess: () => {
                          setExternalOpen(false);
                        },
                      },
                    );
                  }}
                >
                  <HandCoins aria-hidden="true" />
                  {t('manualReview.externalDialog.confirm')}
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </CardContent>
    </Card>
  );
}

function ReviewSkeleton(): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="space-y-6" role="status" aria-label={t('manualReview.skeleton.ariaLabel')}>
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-7 w-72" />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
        <div className="space-y-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-56" />
          <Skeleton className="h-40" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-96" />
          <Skeleton className="h-48" />
        </div>
      </div>
    </div>
  );
}
